import { useSyncExternalStore } from 'react'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type { CashDrawerIdInput, CashDrawerWorkstation } from './cash-drawers.types'

type PairingCandidate = Pick<CashDrawerWorkstation, 'id' | 'is_active'> & {
  location?: Pick<CashDrawerWorkstation['location'], 'id' | 'is_active'> | null
}

const CASH_DRAWER_DEVICE_PAIRING_EVENT = 'onestem:cash-drawer-device-pairing'
const CASH_DRAWER_DEVICE_PAIRING_KEY_PREFIX = 'onestem.cash-drawer-workstation'
const inMemoryPairings = new Map<string, number | null>()

export function getCashDrawerDevicePairingKey(organizationId: OrganizationIdInput) {
  return `${CASH_DRAWER_DEVICE_PAIRING_KEY_PREFIX}:${organizationId}`
}

export function readCashDrawerDevicePairing(organizationId: OrganizationIdInput) {
  if (!organizationId) return null

  const key = getCashDrawerDevicePairingKey(organizationId)

  if (inMemoryPairings.has(key)) return inMemoryPairings.get(key) ?? null

  try {
    if (!globalThis.localStorage) return null

    const storedValue = globalThis.localStorage?.getItem(key)
    const workstationId = Number(storedValue)

    return workstationId > 0 ? workstationId : null
  } catch {
    return null
  }
}

export function writeCashDrawerDevicePairing(
  organizationId: OrganizationIdInput,
  workstationId: CashDrawerIdInput
) {
  if (!organizationId) return

  const key = getCashDrawerDevicePairingKey(organizationId)
  const normalizedWorkstationId = Number(workstationId) || null

  try {
    if (!globalThis.localStorage) throw new Error('Browser storage unavailable')

    if (normalizedWorkstationId) {
      globalThis.localStorage.setItem(key, String(normalizedWorkstationId))
    } else {
      globalThis.localStorage.removeItem(key)
    }
    inMemoryPairings.delete(key)
  } catch {
    // The in-memory pairing still supports this tab when storage is restricted.
    inMemoryPairings.set(key, normalizedWorkstationId)
  }

  if (globalThis.CustomEvent) {
    globalThis.dispatchEvent?.(
      new globalThis.CustomEvent(CASH_DRAWER_DEVICE_PAIRING_EVENT, {
        detail: { organizationId: String(organizationId) },
      })
    )
  }
}

function subscribeToCashDrawerDevicePairing(onStoreChange: () => void) {
  globalThis.addEventListener?.('storage', onStoreChange)
  globalThis.addEventListener?.(CASH_DRAWER_DEVICE_PAIRING_EVENT, onStoreChange)

  return () => {
    globalThis.removeEventListener?.('storage', onStoreChange)
    globalThis.removeEventListener?.(CASH_DRAWER_DEVICE_PAIRING_EVENT, onStoreChange)
  }
}

export function useCashDrawerDevicePairing(organizationId: OrganizationIdInput) {
  const workstationId = useSyncExternalStore(
    subscribeToCashDrawerDevicePairing,
    () => readCashDrawerDevicePairing(organizationId),
    () => null
  )

  return {
    workstationId,
    pairWorkstation: (nextWorkstationId: CashDrawerIdInput) =>
      writeCashDrawerDevicePairing(organizationId, nextWorkstationId),
    clearPairing: () => writeCashDrawerDevicePairing(organizationId, null),
  }
}

const isUsableWorkstation = (workstation: PairingCandidate) =>
  workstation.is_active && workstation.location?.is_active

// Without a usable pairing, a location (or organization) with exactly one active
// register uses it. This never writes a pairing, so adding a register later asks again.
export function findPairedCashDrawerWorkstation<T extends PairingCandidate>(
  workstations: T[],
  workstationId: CashDrawerIdInput,
  locationId?: CashDrawerIdInput
) {
  const pairedWorkstation = workstations.find(
    (workstation) =>
      Number(workstation.id) === Number(workstationId) && isUsableWorkstation(workstation)
  )

  if (pairedWorkstation) return pairedWorkstation

  const candidates = workstations.filter(
    (workstation) =>
      isUsableWorkstation(workstation) &&
      (!locationId || Number(workstation.location?.id) === Number(locationId))
  )

  return candidates.length === 1 ? candidates[0] : null
}

export function pairedCashDrawerMatchesLocation(
  workstation: { location?: { id: number } | null } | null | undefined,
  locationId: CashDrawerIdInput
) {
  return !locationId || Number(workstation?.location?.id) === Number(locationId)
}
